<template>
  <div class="fuelling-readiness">
    <div class="fr-head">
      <div>
        <h3 class="fr-title">Fuelling readiness</h3>
        <p class="fr-sub">
          A player can only get a fuelling estimate once their <strong>sex</strong>
          and <strong>date of birth</strong> are on record — and, for under-18s,
          <strong>parent/guardian consent</strong>. Set them here.
        </p>
      </div>
      <button class="fr-refresh" @click="load" :disabled="loading" title="Reload">↻</button>
    </div>

    <!-- Summary -->
    <div v-if="summary" class="fr-summary">
      <div class="fr-stat ready">
        <span class="fr-stat-num">{{ summary.ready }}/{{ eligibleTotal }}</span>
        <span class="fr-stat-label">Ready</span>
      </div>
      <div class="fr-stat" :class="{ warn: summary.needs_sex > 0 }">
        <span class="fr-stat-num">{{ summary.needs_sex }}</span>
        <span class="fr-stat-label">Need sex</span>
      </div>
      <div class="fr-stat" :class="{ warn: summary.needs_dob > 0 }">
        <span class="fr-stat-num">{{ summary.needs_dob }}</span>
        <span class="fr-stat-label">Need DOB</span>
      </div>
      <div class="fr-stat" :class="{ warn: summary.needs_consent > 0 }">
        <span class="fr-stat-num">{{ summary.needs_consent }}</span>
        <span class="fr-stat-label">Need consent</span>
      </div>
      <div class="fr-stat muted">
        <span class="fr-stat-num">{{ summary.foundation }}</span>
        <span class="fr-stat-label">Foundation (N/A)</span>
      </div>
    </div>

    <div class="fr-toolbar">
      <label class="fr-check">
        <input type="checkbox" v-model="onlyIncomplete" />
        <span>Show only players needing setup</span>
      </label>
      <div class="fr-actions">
        <span v-if="dirtyCount" class="fr-dirty">{{ dirtyCount }} unsaved</span>
        <button v-if="dirtyCount" class="fr-btn ghost" @click="resetEdits" :disabled="saving">Discard</button>
        <button class="fr-btn primary" @click="save" :disabled="!dirtyCount || saving">
          {{ saving ? "Saving…" : "Save changes" }}
        </button>
      </div>
    </div>

    <p v-if="error" class="fr-error">{{ error }}</p>
    <div v-if="loading" class="fr-loading">Loading squad…</div>

    <div v-else class="fr-table-wrap">
      <table class="fr-table">
        <thead>
          <tr>
            <th class="col-name">Player</th>
            <th>Sex</th>
            <th>Date of birth</th>
            <th>Consent (U18)</th>
            <th>Latest weight</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in visiblePlayers" :key="p.id" :class="{ 'row-foundation': p.phase === 'foundation' }">
            <td class="col-name">
              <span class="p-name">{{ p.name }}</span>
              <span class="p-meta">
                {{ p.team || "No team" }} ·
                <template v-if="p.phase === 'foundation'">Foundation</template>
                <template v-else>Performance</template>
                <template v-if="p.band_age"> · U{{ p.band_age }}</template>
              </span>
            </td>

            <td>
              <select class="fr-input" :value="cur(p, 'gender')" :disabled="p.phase === 'foundation'"
                @change="edit(p.id, 'gender', $event.target.value)">
                <option value="">—</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </td>

            <td>
              <input type="date" class="fr-input" :value="cur(p, 'dob') || ''"
                :disabled="p.phase === 'foundation'"
                @change="edit(p.id, 'dob', $event.target.value)" />
            </td>

            <td>
              <label v-if="p.phase === 'performance' && p.under_18" class="fr-check tight">
                <input type="checkbox" :checked="cur(p, 'consented')"
                  @change="edit(p.id, 'consented', $event.target.checked)" />
                <span>{{ cur(p, 'consented') ? "Given" : "Not given" }}</span>
              </label>
              <span v-else class="fr-na">{{ p.phase === 'foundation' ? "—" : "18+" }}</span>
            </td>

            <td>
              <span v-if="p.latest_weight_kg">{{ p.latest_weight_kg }} kg</span>
              <span v-else class="fr-na">—</span>
            </td>

            <td>
              <span class="fr-badge" :class="badgeClass(p)">{{ badgeLabel(p) }}</span>
            </td>
          </tr>
          <tr v-if="!visiblePlayers.length">
            <td colspan="6" class="fr-empty">
              {{ onlyIncomplete ? "Everyone eligible is set up 🎉" : "No players found." }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "FuellingReadiness",
  data() {
    return {
      players: [],
      summary: null,
      loading: false,
      saving: false,
      error: "",
      onlyIncomplete: false,
      // id -> { gender?, dob?, consented? } of unsaved edits
      edits: {},
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },
    isCoach() {
      const pos = this.getProfile?.position;
      return this.getProfile?.userType === "Coach" || pos === "Coach" || pos === "HeadCoach";
    },
    clubId() {
      return this.getProfile?.club_id || this.getProfile?.clubId;
    },
    eligibleTotal() {
      return this.summary ? this.summary.ready + this.summary.incomplete : 0;
    },
    dirtyCount() {
      return Object.keys(this.edits).length;
    },
    visiblePlayers() {
      if (!this.onlyIncomplete) return this.players;
      return this.players.filter((p) => p.status === "incomplete");
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      this.edits = {};
      try {
        const clubIdValue = this.isAdmin ? this.clubId : null;
        const coachId = this.isCoach && !this.isAdmin ? this.getProfile?.id : null;
        const userType = this.isCoach && !this.isAdmin ? "Coach" : "Admin";
        const res = await Nutrition.getSquadReadiness(clubIdValue, coachId, userType);
        const payload = res.data ?? res;
        if (payload && payload.success) {
          this.players = payload.data || [];
          this.summary = payload.summary || null;
        } else {
          this.error = (payload && payload.error) || "Could not load squad readiness.";
        }
      } catch (e) {
        this.error = "Could not load squad readiness.";
      } finally {
        this.loading = false;
      }
    },
    // Current value for a field: unsaved edit if present, else server value.
    cur(player, field) {
      const e = this.edits[player.id];
      if (e && Object.prototype.hasOwnProperty.call(e, field)) return e[field];
      return player[field];
    },
    edit(id, field, value) {
      this.edits = {
        ...this.edits,
        [id]: { ...(this.edits[id] || {}), [field]: value },
      };
    },
    resetEdits() {
      this.edits = {};
    },
    badgeClass(p) {
      if (p.phase === "foundation") return "grey";
      return p.status === "ready" ? "green" : "amber";
    },
    badgeLabel(p) {
      if (p.phase === "foundation") return "N/A";
      if (p.status === "ready") return "Ready";
      const map = { sex: "sex", dob: "DOB", consent: "consent" };
      return "Needs " + (p.missing || []).map((m) => map[m] || m).join(" + ");
    },
    async save() {
      if (!this.dirtyCount) return;
      this.saving = true;
      this.error = "";
      try {
        const updates = Object.keys(this.edits)
          .map((id) => ({ user_id: Number(id), ...this.edits[id] }))
          .filter((u) => u.gender !== undefined || u.dob !== undefined || u.consented !== undefined);
        const res = await Nutrition.bulkSetPlayerBasics(updates, this.getProfile?.id || null);
        const payload = res.data ?? res;
        if (payload && payload.success) {
          await this.load();
        } else {
          this.error = (payload && payload.error) || "Some changes could not be saved.";
        }
      } catch (e) {
        this.error = "Could not save changes.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.fuelling-readiness {
  padding: 4px 2px 24px;
}
.fr-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.fr-title {
  margin: 0 0 4px;
  font-size: 1.25rem;
  color: #1a1a1a;
}
.fr-sub {
  margin: 0;
  color: #555;
  font-size: 0.9rem;
  max-width: 640px;
  line-height: 1.45;
}
.fr-refresh {
  background: #f1f3f5;
  border: 1px solid #dde1e5;
  border-radius: 8px;
  width: 38px;
  height: 38px;
  cursor: pointer;
  font-size: 1.1rem;
  color: #333;
  flex: 0 0 auto;
}
.fr-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 18px 0 8px;
}
.fr-stat {
  background: #f8f9fa;
  border: 1px solid #edf0f2;
  border-radius: 12px;
  padding: 12px 16px;
  min-width: 92px;
  text-align: center;
}
.fr-stat.ready {
  background: #eafaf0;
  border-color: #b7e9c9;
}
.fr-stat.warn {
  background: #fff6e8;
  border-color: #f6d9a6;
}
.fr-stat.muted {
  opacity: 0.75;
}
.fr-stat-num {
  display: block;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
}
.fr-stat-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7278;
  margin-top: 2px;
}
.fr-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 14px 0 10px;
  flex-wrap: wrap;
}
.fr-check {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 0.9rem;
  cursor: pointer;
  margin: 0;
}
.fr-check.tight {
  font-size: 0.82rem;
}
.fr-check input {
  width: auto;
  margin: 0;
}
.fr-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.fr-dirty {
  font-size: 0.82rem;
  color: #b26a00;
}
.fr-btn {
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  border: 1px solid transparent;
}
.fr-btn.primary {
  background: var(--brand-color, #2b61d6);
  color: #fff;
}
.fr-btn.primary:disabled {
  opacity: 0.5;
  cursor: default;
}
.fr-btn.ghost {
  background: #fff;
  border-color: #d5d9dd;
  color: #444;
}
.fr-error {
  color: #c0392b;
  font-size: 0.88rem;
  margin: 6px 0;
}
.fr-loading,
.fr-empty {
  text-align: center;
  color: #7a7f84;
  padding: 28px 0;
}
.fr-table-wrap {
  overflow-x: auto;
  border: 1px solid #edf0f2;
  border-radius: 12px;
}
.fr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 720px;
}
.fr-table th {
  text-align: left;
  background: #f8f9fa;
  color: #5a6067;
  font-weight: 600;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 10px 12px;
  border-bottom: 1px solid #edf0f2;
}
.fr-table td {
  padding: 9px 12px;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
  color: #2c3136;
}
.fr-table tr:last-child td {
  border-bottom: none;
}
.row-foundation td {
  background: #fbfbfc;
  color: #9aa0a6;
}
.col-name {
  min-width: 190px;
}
.p-name {
  display: block;
  font-weight: 600;
  color: #1a1a1a;
}
.row-foundation .p-name {
  color: #6b7278;
}
.p-meta {
  display: block;
  font-size: 0.76rem;
  color: #868c92;
  margin-top: 1px;
}
.fr-input {
  border: 1px solid #d5d9dd;
  border-radius: 7px;
  padding: 6px 8px;
  font-size: 0.86rem;
  color: #2c3136;
  background: #fff;
  min-width: 84px;
}
.fr-input:disabled {
  background: #f4f5f6;
  color: #b0b4b8;
  cursor: not-allowed;
}
.fr-na {
  color: #aab0b5;
}
.fr-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
}
.fr-badge.green {
  background: #e4f7ec;
  color: #1c7a45;
}
.fr-badge.amber {
  background: #fff2dd;
  color: #a8680a;
}
.fr-badge.grey {
  background: #eef0f2;
  color: #8a9096;
}
</style>
